import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import api from '../services/api';
import AppLayout from '../components/AppLayout';
import { toast } from 'react-toastify';
import { routes } from '../lib/routes';
import { labelTipo, labelStatus } from '../lib/protocoloLabels';
import './DetalheProtocolo.css';

const STAFF = ['admin', 'gestor', 'analista'];

function statusPillClass(status) {
  if (status === 'em_analise' || status === 'em_exigencia') return 'protocolo-status-pill protocolo-status-pill--em_analise';
  if (status === 'aprovado') return 'protocolo-status-pill protocolo-status-pill--aprovado';
  if (status === 'indeferido') return 'protocolo-status-pill protocolo-status-pill--indeferido';
  return 'protocolo-status-pill protocolo-status-pill--default';
}

export default function DetalheProtocolo() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: user } = useQuery('me', () => api.get('/usuarios/me').then((r) => r.data), { retry: false });

  const { data: protocolo, isLoading: loadingProt } = useQuery(['protocolo', id], () => api.get(`/protocolos/${id}`).then((r) => r.data));
  const { data: documentos } = useQuery(['documentos', id], () => api.get(`/documentos/${id}`).then((r) => r.data));

  const uploadDocumento = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('nome', file.name);
    try {
      await api.post(`/documentos/upload/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      queryClient.invalidateQueries(['documentos', id]);
      toast.success('Arquivo enviado com sucesso.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha no upload.');
    }
    e.target.value = '';
  };

  const baixar = async (doc) => {
    try {
      const res = await api.get(`/documentos/file/${doc.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.nome || `documento-${doc.id}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Não foi possível baixar o arquivo.');
    }
  };

  const podeAnalisar = user && STAFF.includes(user.tipo);

  const subtitleNode = loadingProt ? (
    'Carregando…'
  ) : protocolo ? (
    <>
      <span className="protocolo-header-meta">{labelTipo(protocolo.tipo)}</span>
      <span className="protocolo-breadcrumb-sep" aria-hidden>
        {' '}
        ·{' '}
      </span>
      <span className={statusPillClass(protocolo.status)}>{labelStatus(protocolo.status)}</span>
    </>
  ) : null;

  return (
    <AppLayout title={protocolo?.numero || 'Protocolo'} subtitle={subtitleNode}>
      <div className="protocolo-page">
        <nav className="protocolo-breadcrumb" aria-label="Navegação secundária">
          <button type="button" className="sispro-btn sispro-btn--secondary sispro-btn--sm" onClick={() => navigate(routes.protocolos)}>
            ← Lista de protocolos
          </button>
          <span className="protocolo-breadcrumb-sep" aria-hidden>
            /
          </span>
          <Link to={routes.dashboard} className="sispro-btn sispro-btn--ghost sispro-btn--sm">
            Dashboard
          </Link>
        </nav>

        <section className="panel protocolo-resumo" aria-labelledby="protocolo-resumo-heading">
          <div className="protocolo-resumo-head">
            <h2 id="protocolo-resumo-heading">Dados do processo</h2>
          </div>
          {loadingProt ? (
            <div className="protocolo-dl-grid" style={{ padding: '1.25rem' }}>
              <div className="protocolo-skel" style={{ width: '100%' }} />
            </div>
          ) : (
            <dl className="protocolo-dl-grid">
              <div className="protocolo-dl-item">
                <dt>Tipo</dt>
                <dd>{labelTipo(protocolo?.tipo)}</dd>
              </div>
              <div className="protocolo-dl-item">
                <dt>Status</dt>
                <dd>
                  <span className={statusPillClass(protocolo?.status)}>{labelStatus(protocolo?.status)}</span>
                </dd>
              </div>
              <div className="protocolo-dl-item">
                <dt>Data de abertura</dt>
                <dd>{protocolo?.data_abertura ? new Date(protocolo.data_abertura).toLocaleDateString('pt-BR') : '—'}</dd>
              </div>
              <div className="protocolo-dl-item">
                <dt>Área construída</dt>
                <dd>{protocolo?.area_construida != null ? `${protocolo.area_construida} m²` : '—'}</dd>
              </div>
            </dl>
          )}
          <div className="protocolo-resumo-actions">
            <Link to={routes.formulario(id)} className="sispro-btn sispro-btn--secondary sispro-btn--sm">
              Formulário dinâmico
            </Link>
            {podeAnalisar ? (
              <Link to={routes.analise(id)} className="sispro-btn sispro-btn--primary sispro-btn--sm">
                Análise técnica
              </Link>
            ) : null}
          </div>
        </section>

        <section className="panel protocolo-docs" aria-labelledby="protocolo-docs-heading">
          <div className="protocolo-docs-head">
            <h2 id="protocolo-docs-heading">Documentos</h2>
          </div>

          <div className="protocolo-upload">
            <p className="protocolo-upload-hint">
              Envie PDF, imagens, DWG ou DOC. Tamanho máximo conforme política do servidor.
            </p>
            <label className="protocolo-file-trigger sispro-btn sispro-btn--secondary sispro-btn--sm">
              <input type="file" accept=".pdf,.dwg,.jpg,.jpeg,.png,.doc,.docx" onChange={uploadDocumento} />
              <span>Escolher arquivo</span>
            </label>
          </div>

          <div className="protocolo-table-wrap">
            <table className="protocolo-table">
              <thead>
                <tr>
                  <th scope="col">Nome</th>
                  <th scope="col">Tipo</th>
                  <th scope="col">Status</th>
                  <th scope="col">Ações</th>
                </tr>
              </thead>
              <tbody>
                {documentos?.length ? (
                  documentos.map((doc) => (
                    <tr key={doc.id}>
                      <td>
                        <span className="protocolo-doc-nome">{doc.nome}</span>
                      </td>
                      <td>
                        <span className="protocolo-doc-meta">{doc.tipo || '—'}</span>
                      </td>
                      <td>
                        <span className="protocolo-doc-meta protocolo-capitalize">{doc.status}</span>
                      </td>
                      <td>
                        <button type="button" className="sispro-btn sispro-btn--secondary sispro-btn--sm" onClick={() => baixar(doc)}>
                          Baixar
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="sispro-empty protocolo-empty-cell">
                      Nenhum documento anexado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </AppLayout>
  );
}
